import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Project, ProjectStatus, ProjectFormData } from '../../types/Project';
import { projectService } from '../../services/projectService';
import { taskService, Task } from '../../services/taskService';
import { tagService, Tag } from '../../services/tagService';
import TaskRow from '../tasks/TaskRow';
import PageHeader from '../common/PageHeader';
import './ProjectEdit.css';

interface ProjectEditProps {
  user: User | null;
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onSignOut?: () => void;
  onShowAuthModal?: () => void;
  authLoading?: boolean;
}

const ProjectEdit: React.FC<ProjectEditProps> = ({
  user,
  sidebarOpen = false,
  onToggleSidebar,
  onSignOut,
  onShowAuthModal,
  authLoading = false
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewProject = !id || id === 'new';

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(!isNewProject);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>(ProjectStatus.NOT_STARTED);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [progressPercentage, setProgressPercentage] = useState('');
  const [notes, setNotes] = useState('');

  // Tasks state
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [taskTags, setTaskTags] = useState<{ [taskId: number]: string[] }>({});
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);

  // Load project data if editing
  useEffect(() => {
    if (user && id && !isNewProject) {
      loadProject();
      loadProjectTasks();
      loadAvailableTags();
    } else if (isNewProject) {
      setLoading(false);
      loadAvailableTags();
    }
  }, [user, id, isNewProject]);

  // Handle clicks outside menu dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Helper function to format dates for input fields
  const formatDateForInput = (dateString: string | undefined | null): string => {
    if (!dateString) return '';
    
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Otherwise, parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const loadProject = async () => {
    if (!user || !id || isNewProject) return;

    setLoading(true);
    try {
      const response = await projectService.getProjectById(id);

      if (response.success && response.project) {
        const convertedProject = projectService.convertDatabaseProject(response.project);
        setProject(convertedProject);

        // Populate form fields
        setName(convertedProject.name);
        setDescription(convertedProject.description || '');
        setStatus(convertedProject.status);
        setStartDate(formatDateForInput(convertedProject.start_date));
        setTargetDate(formatDateForInput(convertedProject.target_date));
        setProgressPercentage(convertedProject.progress_percentage.toString());
        setNotes(convertedProject.notes || '');

        setError(null);
      } else {
        setError('Project not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load project');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectTasks = async () => {
    if (!id || isNewProject) return;

    setTasksLoading(true);
    try {
      const response = await taskService.getProjectTasks(id);
      if (response.success && response.tasks) {
        setProjectTasks(response.tasks);
        
        // Extract tags from tasks
        const tagsMap: { [taskId: number]: string[] } = {};
        response.tasks.forEach((task: Task) => {
          tagsMap[task.id] = task.tags || [];
        });
        setTaskTags(tagsMap);
      }
    } catch (err: any) {
      console.error('Error loading project tasks:', err);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const handleToggleTaskStatus = async (task: Task) => {
    try {
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        await loadProjectTasks();
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task');
    }
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleTaskMenuAction = async (task: Task, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'log-time') => {
    if (action === 'delete') {
      if (!window.confirm('Delete this task?')) return;
      
      const response = await taskService.deleteTask(task.id.toString());
      
      if (response.success) {
        await loadProjectTasks();
      } else {
        setError('Failed to delete task. Please try again.');
      }
    }
    // Other actions can be handled similarly if needed
  };

  const handleSave = async () => {
    if (!user) return;

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData: ProjectFormData = {
        name: name.trim(),
        description: description.trim() || undefined,
        status,
        start_date: startDate || undefined,
        target_date: targetDate || undefined,
        progress_percentage: progressPercentage ? parseInt(progressPercentage) : undefined,
        notes: notes.trim() || undefined
      };

      if (isNewProject) {
        // Create new project
        const result = await projectService.createProject(user.id.toString(), formData);

        if (result.success) {
          setSuccessMessage('Project created successfully!');
          
          // Navigate back to projects list after a short delay
          setTimeout(() => {
            navigate('/projects', { replace: true });
          }, 1000);
        } else {
          throw new Error(result.message || 'Failed to create project');
        }
      } else {
        // Update existing project
        const updateData: any = {
          id: project!.id,
          name: name.trim(),
          description: description.trim() || undefined,
          status,
          start_date: startDate || undefined,
          target_date: targetDate || undefined,
          progress_percentage: progressPercentage ? parseInt(progressPercentage) : undefined,
          notes: notes.trim() || undefined
        };

        const result = await projectService.updateProject(updateData);

        if (result.success) {
          setSuccessMessage('Project updated successfully!');
          
          // Navigate back to projects list after a short delay
          setTimeout(() => {
            navigate('/projects', { replace: true });
          }, 1000);
        } else {
          throw new Error('Failed to update project');
        }
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${isNewProject ? 'create' : 'update'} project`);
      console.error(`Error ${isNewProject ? 'creating' : 'updating'} project:`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects', { replace: true });
  };

  const handleDelete = async () => {
    if (!user || !project || isNewProject) return;

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await projectService.deleteProject(project.id);

      if (result.success) {
        setSuccessMessage('Project deleted successfully!');
        
        // Navigate back to projects list after a short delay
        setTimeout(() => {
          navigate('/projects', { replace: true });
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to delete project');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setShowMenu(false);
    }
  };

  // Early returns
  if (!user) {
    return (
      <div className="project-edit-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar || (() => {})}
          centerContent={<h1 className="project-edit-header-title">{isNewProject ? 'New Project' : 'Edit Project'}</h1>}
        />
        <div className="project-edit-auth-required">
          <p>Please sign in to {isNewProject ? 'create' : 'edit'} projects.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="project-edit-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar || (() => {})}
          centerContent={<h1 className="project-edit-header-title">Loading...</h1>}
        />
        <div className="project-edit-loading-container">
          <div className="project-edit-loading-spinner"></div>
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error && !isNewProject && !project) {
    return (
      <div className="project-edit-page">
        <PageHeader
          onToggleSidebar={onToggleSidebar || (() => {})}
          centerContent={<h1 className="project-edit-header-title">Project Not Found</h1>}
        />
        <div className="project-edit-error-container">
          <p>{error}</p>
          <button onClick={() => navigate('/projects')} className="project-edit-back-btn">
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const rightActions = !isNewProject ? (
    <div className="project-edit-menu-container" ref={menuRef}>
      <button
        className="project-edit-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
        disabled={deleting}
      >
        ⋮
      </button>
      {showMenu && (
        <div className="project-edit-menu-dropdown">
          <button
            className="project-edit-menu-item project-edit-menu-item-danger"
            onClick={() => {
              setShowDeleteConfirm(true);
              setShowMenu(false);
            }}
            disabled={deleting}
          >
            Delete Project
          </button>
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <div className="project-edit-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar || (() => {})}
        centerContent={<h1 className="project-edit-header-title">{isNewProject ? 'New Project' : 'Edit Project'}</h1>}
        rightActions={rightActions}
      />

      {error && (
        <div className="project-edit-error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="project-edit-success-banner">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="project-edit-delete-confirm-overlay">
          <div className="project-edit-delete-confirm-dialog">
            <h3>Delete Project</h3>
            <p>Are you sure you want to delete "{project?.name}"? This action cannot be undone.</p>
            <div className="project-edit-delete-confirm-actions">
              <button
                className="project-edit-cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="project-edit-delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="project-edit-content">
        <div className="project-edit-form">
          {/* Basic Information Section */}
          <div className="project-edit-form-section">
            <h2>Basic Information</h2>

            <div className="project-edit-form-group">
              <label>Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name..."
                className="project-edit-form-input"
              />
            </div>

            <div className="project-edit-form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your project..."
                rows={4}
                className="project-edit-form-textarea"
              />
            </div>

            <div className="project-edit-form-row">
              <div className="project-edit-form-group">
                <label>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                  className="project-edit-form-select"
                >
                  {Object.values(ProjectStatus).map(st => (
                    <option key={st} value={st}>
                      {st.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              <div className="project-edit-form-group">
                <label>Progress (%)</label>
                <input
                  type="text"
                  value={progressPercentage}
                  onChange={(e) => setProgressPercentage(e.target.value)}
                  placeholder="0"
                  className="project-edit-form-input"
                />
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="project-edit-form-section">
            <h2>Timeline</h2>

            <div className="project-edit-form-row">
              <div className="project-edit-form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="project-edit-form-input"
                />
              </div>

              <div className="project-edit-form-group">
                <label>Target Date</label>
                <input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="project-edit-form-input"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="project-edit-form-section">
            <h2>Additional Notes</h2>

            <div className="project-edit-form-group">
              <label>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes or context..."
                rows={4}
                className="project-edit-form-textarea"
              />
            </div>
          </div>

          {/* Project Tasks Section */}
          {!isNewProject && (
            <div className="project-edit-form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2>Project Tasks ({projectTasks.length})</h2>
                <button
                  type="button"
                  onClick={() => navigate(`/tasks?create=true&project_id=${id}`)}
                  className="project-edit-add-task-btn"
                  style={{
                    background: 'none',
                    border: '1px solid #e0e0e0',
                    color: '#666666',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  + Create Task
                </button>
              </div>

              {tasksLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#666666' }}>
                  Loading tasks...
                </div>
              ) : projectTasks.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#666666' }}>
                  <p>No tasks assigned to this project yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {projectTasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onToggleStatus={handleToggleTaskStatus}
                      onTaskClick={handleTaskClick}
                      onMenuAction={handleTaskMenuAction}
                      taskTags={taskTags[task.id] || []}
                      availableTags={availableTags}
                      showMenu={true}
                      showStatusToggle={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="project-edit-form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="project-edit-cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="project-edit-save-btn"
              disabled={saving || !name.trim()}
            >
              {saving ? 'Saving...' : (isNewProject ? 'Create Project' : 'Save Changes')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectEdit;

